// ---------------------------------------------------------------------------
// FooterBlock Renderer (editor key: FooterBlock)
// ---------------------------------------------------------------------------
// Renders a multi-column site footer with company brand, tagline, link
// columns, and a copyright line. Editor input for the columns is a
// pipe-delimited textarea where `---` on its own line separates columns.
// Each column has a title line followed by `Label | URL` link rows.
// URLs route through the URL safelist so `javascript:` collapses to `#`.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface FooterBlockProps {
  companyName?: string;
  tagline?: string;
  columns?: string;
  copyright?: string;
}

interface FooterLink {
  label: string;
  href: string;
}

interface FooterColumn {
  title: string;
  links: FooterLink[];
}

function parseColumns(value: unknown): FooterColumn[] {
  if (typeof value !== "string") return [];

  const blocks = value
    .split(/\n?---\n?/)
    .map((block) => block.trim())
    .filter((block) => block.length > 0);

  return blocks
    .map((block) => {
      const lines = block
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
      const title = lines[0] ?? "";
      const links: FooterLink[] = lines.slice(1).map((line) => {
        const [label, href] = line.split("|").map((part) => part.trim());
        return {
          label: label ?? "",
          href: href ?? "",
        };
      }).filter((link) => link.label.length > 0);
      return { title, links };
    })
    .filter((column) => column.title.length > 0 || column.links.length > 0);
}

function renderLink(link: FooterLink): string {
  if (!link.href) {
    return `      <li class="ease-footer-block__item"><span class="ease-footer-block__label">${text(link.label)}</span></li>`;
  }
  return `      <li class="ease-footer-block__item"><a class="ease-footer-block__link" href="${url(link.href)}">${text(link.label)}</a></li>`;
}

function renderColumn(column: FooterColumn): string {
  const linksHtml = column.links.length > 0
    ? `\n    <ul class="ease-footer-block__list" role="list">
${column.links.map(renderLink).join("\n")}
    </ul>`
    : "";
  const titleHtml = column.title
    ? `<h4 class="ease-footer-block__column-title">${text(column.title)}</h4>`
    : "";

  return `  <div class="ease-footer-block__column">
    ${titleHtml}${linksHtml}
  </div>`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as FooterBlockProps;

  const companyName = typeof p.companyName === "string" ? p.companyName.trim() : "";
  const tagline = typeof p.tagline === "string" ? p.tagline.trim() : "";
  const copyright = typeof p.copyright === "string" ? p.copyright.trim() : "";
  const columns = parseColumns(p.columns);

  if (!companyName && !tagline && !copyright && columns.length === 0) {
    return "<!-- footer-block missing content -->";
  }

  const brandHtml = companyName || tagline
    ? `  <div class="ease-footer-block__brand">
    ${companyName ? `<p class="ease-footer-block__company">${text(companyName)}</p>` : ""}
    ${tagline ? `<p class="ease-footer-block__tagline">${text(tagline)}</p>` : ""}
  </div>`
    : "";

  const columnsHtml = columns.map(renderColumn).join("\n");

  const copyrightHtml = copyright
    ? `\n  <div class="ease-footer-block__copyright">
    <small>${text(copyright)}</small>
  </div>`
    : "";

  return `<footer class="ease-section ease-footer-block" data-columns="${attr(String(columns.length))}">
${brandHtml}
${columnsHtml}${copyrightHtml}
</footer>`;
}

registerRenderer("footer-block", { render });
